import { watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useAuthStore from '@/stores/authStore'
import { logoutReason } from '@/composables/useAuth'
import { queryClient, idbQueryStorage } from '@/plugins/vue-query'
import { setDatadogUserAndAccount } from '@/plugins/datadog'
import { clearSurvicateUser } from '@/plugins/survicate'
import { store as legacyStoreUntyped } from '@old/store'

const legacyStore = legacyStoreUntyped as {
  dispatch(action: 'set_state_property', payload: { property: string; data: unknown }): Promise<unknown>
}

// LEGACY: the global dialogs (payments/media/amortization/respite + the
// profile/message dialogs) all open via `immediate: true` watchers on these
// Vuex keys — a key still populated at an auth transition instantly REOPENS
// the dialog when App.vue remounts the component (v-if / :key). The dialogs
// only clean their own key when closed by the user, and unmounting skips that,
// so these keys are reset on EVERY auth transition (login and logout — a
// cross-tab login must not resurrect a dialog opened before the transition).
// Shapes must mirror the store's initial state exactly — the dialog watchers
// dereference nested paths (amortization.multiple.documents, respite.document)
// and crash on `{}`. respite/amortization were missing from the old
// base_data (latent gap in the old app). Dies with the legacy views.
const LEGACY_DIALOG_STATE: Record<string, unknown> = {
  payment: {},
  media: {},
  profile_dialog: {},
  message_dialog: {},
  respite: { error: '', params: {}, document: {} },
  amortization: {
    error: '',
    params: {},
    document: {},
    multiple: { documents: [], selected: [] },
  },
}

// LEGACY: the implicit logout path (idle timeout / refresh failure) bypasses
// the old user_mixin.logout_user, which owned this reset — without it the
// previous user's documents stay in memory on the login screen (shared-device
// privacy). Field list mirrors logout_user's base_data minus the dialog keys
// above; dies with the legacy views.
const LEGACY_LOGOUT_STATE: Record<string, unknown> = {
  documents: [],
  closed_documents: [],
  pre_login_user_tokens: null,
  public_token: null,
  user: {},
  tickets: [],
  unread_tickets: 0,
  dont_show_profile_dialog: false,
  direct_debit_payments: {},
}

function resetLegacyProperties(state: Record<string, unknown>) {
  for (const [property, data] of Object.entries(state)) {
    // Clone per dispatch — legacy components mutate their state objects in
    // place, and a shared module constant handed to the store would get
    // polluted, turning a later "reset" into a dialog-opening write.
    void legacyStore.dispatch('set_state_property', { property, data: structuredClone(data) })
  }
}

function resetLegacyState() {
  resetLegacyProperties(LEGACY_DIALOG_STATE)
  resetLegacyProperties(LEGACY_LOGOUT_STATE)
  sessionStorage.removeItem('dont_show_profile_dialog')
}

// PERMANENT composable: keeps the TanStack query cache in sync with auth
// transitions. App-agnostic core ported from ada-app-ui — token-type
// classification, /user--extended-data handling, creditor onboarding, guard
// re-validation and return-to are intentionally not ported.
export function useAuthEffects() {
  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()

  // 1) Implicit logout (idle timeout / refresh failure) → flush cache + redirect
  watch(
    () => authStore.selectedToken == null,
    async (loggedOut) => {
      if (!loggedOut || route.meta.public === true) return
      // Clear analytics identity — RUM/NPS must not keep attributing the
      // logged-out session to the previous user (shared-device privacy).
      setDatadogUserAndAccount(null, null)
      clearSurvicateUser()
      // LEGACY: drop the previous user's data (see resetLegacyState). Runs
      // twice on explicit logout (logout_user already resets) — idempotent,
      // harmless.
      resetLegacyState()
      await queryClient.cancelQueries()
      await idbQueryStorage.clear()
      queryClient.clear()

      const reason = logoutReason.value
      logoutReason.value = null
      void router.push(reason != null ? { path: '/authenticate', query: { reason } } : '/authenticate')
    }
  )

  // 1b) LEGACY: close the global dialogs on EVERY auth transition — both
  //     directions. A login (e.g. synced from another tab) does not pass
  //     through the logout watcher above, but a dialog key populated while
  //     logged out (pay-with-OCR on the login screen) would instantly reopen
  //     the dialog on remount via its `immediate: true` watcher. Runs pre-flush
  //     (before re-render), so remounted dialogs see clean state. Trade-off:
  //     an OCR deep-link stash is dropped if the user logs in instead of
  //     paying anonymously — acceptable, the pay-without-login flow is intact.
  watch(
    () => authStore.selectedToken != null,
    () => {
      resetLegacyProperties(LEGACY_DIALOG_STATE)
    }
  )

  // 2) User switch (token_id change) → reset the query cache. NO navigation
  //    here: the push('/') is owned by useLegacyBootstrap's identity watcher
  //    until views are migrated (avoids double navigation).
  watch(
    () => authStore.selectedToken?.payload?.token_id ?? null,
    async (id, oldId) => {
      if (oldId == null || id == null || id === oldId) return
      await queryClient.cancelQueries()
      await idbQueryStorage.clear()
      void queryClient.resetQueries()
    }
  )
}
